import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["body", "collapsedSummary", "questionPanel", "optionInput", "optionLabel", "freeFormInput", "submitButton", "submitButtonLabel", "submitButtonIcon", "errorMessage", "progressLabel", "progressTick", "progressBar"]
  static values = { total: Number, current: Number, taskId: String, collapsible: Boolean, collapsed: Boolean }

  connect() {
    this.togglePinnedHandler = this.togglePinned.bind(this)
    document.addEventListener("ask-user-prompt:toggle", this.togglePinnedHandler)
    this.syncCollapsedState()
    this.showQuestion(this.currentValue)

    // Tray mode: the card is fixed, so keep the tray's in-flow spacer as tall as the
    // card — otherwise the last stretch of the timeline is stuck underneath it.
    this.traySpacer = this.element.closest("[data-ask-user-tray]")?.querySelector("[data-tray-spacer]")
    if (this.traySpacer) {
      this.trayResizeObserver = new ResizeObserver(() => this.syncTraySpacer())
      this.trayResizeObserver.observe(this.element)
      this.syncTraySpacer()
    }
  }

  disconnect() {
    document.removeEventListener("ask-user-prompt:toggle", this.togglePinnedHandler)
    this.trayResizeObserver?.disconnect()
  }

  syncTraySpacer() {
    if (!this.traySpacer) return

    this.traySpacer.style.height = `${this.element.offsetHeight + 16}px`
  }

  collapse() {
    if (!this.collapsibleValue) return

    this.collapsedValue = true
    this.syncCollapsedState()
  }

  expand() {
    if (!this.collapsibleValue) return

    this.collapsedValue = false
    this.syncCollapsedState()
  }

  togglePinned(event) {
    if (event.detail?.taskId !== this.taskIdValue) return

    if (this.collapsibleValue) {
      this.collapsedValue ? this.expand() : this.collapse()
    }

    this.element.scrollIntoView({ block: "nearest", behavior: "smooth" })
  }

  syncCollapsedState() {
    if (!this.collapsibleValue) return

    this.bodyTargets.forEach(target => target.classList.toggle("hidden", this.collapsedValue))
    this.collapsedSummaryTargets.forEach(target => target.classList.toggle("hidden", !this.collapsedValue))
    this.element.classList.toggle("max-h-[min(58dvh,34rem)]", !this.collapsedValue)
    this.element.classList.toggle("sm:max-h-[42vh]", !this.collapsedValue)
  }

  showQuestion(index) {
    this.questionPanelTargets.forEach((panel, i) => {
      panel.classList.toggle("hidden", i !== index)
    })
    this.currentValue = index
    this.updateProgress()
    this.refreshOptionHighlights()
    this.updateSubmitState()
    this.updateSubmitLabel()
  }

  // Keeps the tape's count and ticks (and the unframed bars) in step as
  // questions advance — the server only rendered the starting position.
  updateProgress() {
    this.progressLabelTargets.forEach(label => { label.textContent = this.currentValue + 1 })
    this.progressTickTargets.forEach((tick, i) => {
      tick.classList.toggle("bg-brand-accent/70", i <= this.currentValue)
      tick.classList.toggle("bg-brand-body/10", i > this.currentValue)
    })
    this.progressBarTargets.forEach((bar, i) => {
      bar.classList.toggle("bg-brand-accent/70", i <= this.currentValue)
      bar.classList.toggle("bg-brand-body/10", i > this.currentValue)
    })
  }

  optionChanged() {
    this.refreshOptionHighlights()
    this.updateSubmitState()
  }

  freeFormTyped() {
    const panel = this.currentPanel()
    if (panel && this.getFreeFormValue(panel).length > 0 && this.singleSelectQuestion(panel)) {
      this.clearSelectedOptions(panel)
      this.refreshOptionHighlights()
    }

    this.updateSubmitState()
  }

  updateSubmitState() {
    const panel = this.currentPanel()
    if (!panel) return

    const hasSelection = this.hasSelectedOption(panel)
    const hasFreeForm = this.getFreeFormValue(panel).length > 0
    const answerable = hasSelection || hasFreeForm
    // Same tell as the resolution composer: Submit lights to accent once there
    // is something to send.
    this.submitButtonTargets.forEach(button => {
      button.disabled = !answerable
      button.classList.toggle("btn-primary-action", answerable)
    })
  }

  updateSubmitLabel() {
    const isLastQuestion = this.currentValue >= this.totalValue - 1

    this.submitButtonLabelTargets.forEach(label => {
      label.textContent = isLastQuestion ? "Submit" : "Continue"
    })

    this.submitButtonIconTargets.forEach(icon => {
      icon.classList.toggle("ti-arrow-right", !isLastQuestion)
      icon.classList.toggle("ti-arrow-up", isLastQuestion)
    })
  }

  refreshOptionHighlights() {
    const panel = this.currentPanel()
    if (!panel) return

    // Options render as resolution-style chips: selected ones fill with accent
    // and show a check.
    const labels = panel.querySelectorAll("[data-ask-user-prompt-target='optionLabel']")
    labels.forEach(label => {
      const input = label.querySelector("[data-ask-user-prompt-target='optionInput']")
      const selected = input?.checked === true

      label.classList.toggle("btn-primary-action", selected)
      label.querySelector("[data-option-marker-indicator]")?.classList.toggle("hidden", !selected)
    })
  }

  singleSelectQuestion(panel) {
    return panel.querySelector("[data-ask-user-prompt-target='optionInput'][type='radio']") !== null
  }

  clearSelectedOptions(panel) {
    const inputs = panel.querySelectorAll("[data-ask-user-prompt-target='optionInput']")
    inputs.forEach(input => {
      input.checked = false
    })
  }

  hasSelectedOption(panel) {
    const inputs = panel.querySelectorAll("[data-ask-user-prompt-target='optionInput']")
    return Array.from(inputs).some(input => input.checked && input.value !== "")
  }

  getSelectedOptions(panel) {
    const inputs = panel.querySelectorAll("[data-ask-user-prompt-target='optionInput']")
    return Array.from(inputs)
      .filter(input => input.checked && input.value !== "")
      .map(input => input.value)
  }

  getFreeFormValue(panel) {
    const input = panel.querySelector("[data-ask-user-prompt-target='freeFormInput']")
    return input ? input.value.trim() : ""
  }

  currentPanel() {
    return this.questionPanelTargets[this.currentValue]
  }

  prev() {
    if (this.currentValue > 0) {
      this.showQuestion(this.currentValue - 1)
    }
  }

  async submitAnswer() {
    const panel = this.currentPanel()
    if (!panel) return

    this.clearError(panel)

    const selectedOptions = this.getSelectedOptions(panel)
    const freeForm = this.getFreeFormValue(panel)

    if (selectedOptions.length === 0 && freeForm.length === 0) return

    const formData = new FormData()
    formData.append("question_index", this.currentValue)
    selectedOptions.forEach(opt => formData.append("selected_options[]", opt))
    if (freeForm) formData.append("free_form", freeForm)

    this.submitButtonTargets.forEach(button => {
      button.disabled = true
      button.classList.add("opacity-50")
    })

    try {
      const response = await this.postAnswer(formData)
      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      } else {
        this.showError(panel, "Could not submit your answer. Please try again.")
        this.resetSubmitAfterFailure()
      }
    } catch (_error) {
      this.showError(panel, "Could not submit your answer. Please try again.")
      this.resetSubmitAfterFailure()
    }
  }

  resetSubmitAfterFailure() {
    this.submitButtonTargets.forEach(button => {
      button.classList.remove("opacity-50")
    })
    this.updateSubmitState()
  }

  async skip() {
    const panel = this.currentPanel()
    this.clearError(panel)

    const formData = new FormData()
    formData.append("skip", "true")
    // Carry whatever was typed. Skip used to post `skip=true` alone, so text in
    // the answer box was destroyed without a trace and the task recorded a bare
    // "Skipped" -- including when that text was an instruction to the agent.
    const freeForm = panel ? this.getFreeFormValue(panel) : ""
    if (freeForm) formData.append("free_form", freeForm)

    try {
      const response = await this.postAnswer(formData)
      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      } else {
        this.showError(panel, "Could not skip this question. Please try again.")
      }
    } catch (_error) {
      this.showError(panel, "Could not skip this question. Please try again.")
    }
  }

  async postAnswer(formData) {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    const response = await fetch(`/tasks/${this.taskIdValue}/answer_question`, {
      method: "POST",
      headers: {
        "Accept": "text/vnd.turbo-stream.html",
        "X-CSRF-Token": csrfToken
      },
      body: formData
    })

    return response
  }

  showError(panel, message) {
    const errorMessage = panel?.querySelector("[data-ask-user-prompt-target='errorMessage']")
    if (!errorMessage) return

    errorMessage.textContent = message
    errorMessage.classList.remove("hidden")
  }

  clearError(panel) {
    const errorMessage = panel?.querySelector("[data-ask-user-prompt-target='errorMessage']")
    if (!errorMessage) return

    errorMessage.textContent = ""
    errorMessage.classList.add("hidden")
  }
}
